import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import AdminAuthCard from "@/components/admin/AdminAuthCard";
import JeaInput from "@/components/jea/JeaInput";
import JeaButton from "@/components/jea/JeaButton";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";

const AdminLoginPage = () => {
  const nav = useNavigate();
  const { adminLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Enter your email and password."); return; }
    setError("");
    setLoading(true);
    try {
      const data = await adminLogin(email.trim().toLowerCase(), password);
      nav("/admin/2fa", { state: { pending_token: data.pending_token, email: data.email } });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminAuthCard>
      <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
        <div className="flex items-center gap-2 text-danger text-[13px] mb-1">
          <ShieldAlert size={14} />
          <span>Restricted access. All actions are logged.</span>
        </div>
        <JeaInput
          type="email"
          autoComplete="email"
          placeholder="Admin email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          leftIcon={<Mail size={16} />}
          data-testid="admin-login-email"
        />
        <JeaInput
          type={show ? "text" : "password"}
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          leftIcon={<Lock size={16} />}
          data-testid="admin-login-password"
          rightSlot={
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="p-2 rounded-md text-text-secondary hover:text-accent-pink transition-colors"
              aria-label={show ? "Hide password" : "Show password"}
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
        />

        {error && (
          <div
            className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2"
            data-testid="admin-login-error"
          >
            {error}
          </div>
        )}

        <div className="pt-2">
          <JeaButton
            type="submit"
            size="lg"
            fullWidth
            loading={loading}
            className="!bg-danger !text-white hover:!bg-danger/90"
            data-testid="admin-login-submit"
          >
            Continue
          </JeaButton>
        </div>

        <div className="pt-2 text-center">
          <Link
            to="/login"
            className="text-[12px] text-text-muted hover:text-accent-pink transition-colors"
            data-testid="admin-login-user-link"
          >
            ← Not an admin? User login
          </Link>
        </div>
      </form>
    </AdminAuthCard>
  );
};

export default AdminLoginPage;
